/**
 * Maestro dashboard help — same shape as Forecasting Dashboard Help.
 * User Guide explains the imported grid, Report 9091, and the Consolidated report:
 * which columns come from the Maestro import, which are calculated, and the formulas.
 */
import React, { useState } from 'react';
import { ListenButton } from '../common/AudioGuide';
import HelpVideoTour from '../Help/HelpVideoTour';

const ACCENT = '#0369a1';

const sourceCell = (kind, text) => {
  const color = kind === 'maestro' ? '#15803d' : kind === 'calc' ? '#0369a1' : '#6b7280';
  return <td style={{ padding: '8px', color }}>{text}</td>;
};

const MaestroDashboardHelp = ({ onClose }) => {
  const [helpTab, setHelpTab] = useState('guide');

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', width: '95%', maxWidth: '860px',
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}>
        <div style={{
          padding: '1rem 1.5rem', borderBottom: '1px solid #dee2e6', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', backgroundColor: ACCENT,
        }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'white' }}>
            ❓ Maestro Dashboard Help
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ListenButton compact dark guideId="maestro-reports-help" title="Maestro Dashboard Help" accent={ACCENT} />
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px', border: 'none', borderRadius: '4px',
                backgroundColor: 'rgba(255,255,255,0.2)', color: 'white',
                cursor: 'pointer', fontSize: '14px',
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexShrink: 0, position: 'relative', zIndex: 3, borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
          {[
            { id: 'guide', label: '📖 User Guide' },
            { id: 'flowchart', label: '🔀 Workflow Flowchart' },
            { id: 'tour', label: '🎬 Video Tour' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setHelpTab(t.id)}
              style={{
                padding: '12px 24px', border: 'none', flexShrink: 0,
                borderBottom: helpTab === t.id ? `3px solid ${ACCENT}` : '3px solid transparent',
                backgroundColor: helpTab === t.id ? 'white' : 'transparent',
                color: helpTab === t.id ? ACCENT : '#6b7280',
                fontWeight: helpTab === t.id ? '600' : '400',
                cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          {helpTab === 'flowchart' && <Flowchart />}
          {helpTab === 'guide' && <Guide />}
          {helpTab === 'tour' && (
            <HelpVideoTour
              guideId="maestro-reports-help"
              title="Maestro Dashboard Help"
              accent={ACCENT}
              compact
              showHeader
            />
          )}
        </div>
      </div>
    </div>
  );
};

function Flowchart() {
  const steps = [
    ['#dbeafe', '#3b82f6', '#1e40af', '📥', '1. Export from Maestro', 'Spreadsheet of the job, as of a cut-off'],
    ['#fef3c7', '#f59e0b', '#b45309', '✏️', '2. Optional cut-off and note', 'Helps you recognize the snapshot. Blank is fine.'],
    ['#dcfce7', '#22c55e', '#15803d', '📋', '3. Imported Data grid', 'Every column is stored as Maestro sent it'],
    ['#e0e7ff', '#6366f1', '#4338ca', '🧮', '4. Report 9091', 'Group, sum, then calculate variances and profit'],
    ['#fae8ff', '#c026d3', '#a21caf', '📄', '5. Download', '9091 per company, or Consolidated / Roll-Up'],
  ];
  return (
    <div style={{ padding: '8px 12px 20px' }}>
      <h3 style={{ margin: '0 0 24px 0', textAlign: 'center', color: '#111827' }}>Maestro Dashboard Workflow</h3>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        {steps.map((s, i) => (
          <React.Fragment key={s[4]}>
            <div style={{ padding: '16px 32px', backgroundColor: s[0], borderRadius: '8px', border: `2px solid ${s[1]}`, textAlign: 'center', minWidth: '280px' }}>
              <div style={{ fontSize: '20px', marginBottom: '4px' }}>{s[3]}</div>
              <div style={{ fontWeight: '600', color: s[2] }}>{s[4]}</div>
              <div style={{ fontSize: '12px', color: s[1], marginTop: '4px' }}>{s[5]}</div>
            </div>
            {i < steps.length - 1 && <div style={{ fontSize: '24px', color: '#9ca3af' }}>↓</div>}
          </React.Fragment>
        ))}
      </div>
      <div style={{ marginTop: '32px', padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>📐 Report 9091 calculation flow</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
          {['Orig Bud (Q)', 'Actual (Q)', 'Rev Selling Bud', 'Billing', 'Orig Bud', 'PO Bal', 'Total & PO'].map((label) => (
            <span key={label} style={{ padding: '6px 12px', backgroundColor: '#dcfce7', borderRadius: '4px', fontSize: '12px', color: '#15803d' }}>{label}</span>
          ))}
          <span style={{ color: '#9ca3af' }}>→</span>
          <span style={{ padding: '6px 12px', backgroundColor: '#e0e7ff', borderRadius: '4px', fontSize: '12px', color: '#4338ca', fontWeight: '600' }}>Sum per activity</span>
          <span style={{ color: '#9ca3af' }}>→</span>
          <span style={{ padding: '6px 12px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '12px', color: '#1e40af', fontWeight: '600' }}>Variances + profit</span>
        </div>
      </div>
      <div style={{ marginTop: '16px', padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>📐 Consolidated calculation flow</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
          {['Labour hours', 'Rev Selling Bud', 'Billing', 'Orig Bud', 'Actual+Accrued', 'Total & PO'].map((label) => (
            <span key={label} style={{ padding: '6px 12px', backgroundColor: '#dcfce7', borderRadius: '4px', fontSize: '12px', color: '#15803d' }}>{label}</span>
          ))}
          <span style={{ color: '#9ca3af' }}>→</span>
          <span style={{ padding: '6px 12px', backgroundColor: '#e0e7ff', borderRadius: '4px', fontSize: '12px', color: '#4338ca', fontWeight: '600' }}>Merge companies</span>
          <span style={{ color: '#9ca3af' }}>→</span>
          <span style={{ padding: '6px 12px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '12px', color: '#1e40af', fontWeight: '600' }}>% Spent, profit, add&apos;l bill</span>
        </div>
      </div>
    </div>
  );
}

function Guide() {
  return (
    <>
      <div style={{ marginBottom: '24px' }}>
        <h3 style={sectionTitle}>
          <Step n="1" /> Workflow Overview
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
          <Card tone="blue" icon="📥" title="Import Maestro" body="Upload the Excel export. Cut-off date and note are optional." />
          <Card tone="green" icon="📋" title="Imported Data" body="The grid is the file. Nothing on it is recalculated." />
          <Card tone="amber" icon="📄" title="Report 9091" body="Built from that snapshot: summed lines, then formulas." />
        </div>
        <Callout tone="amber">
          <strong>Cut-off date and note</strong> are labels for you. They do not filter rows, and leaving either one blank does not block the upload. The snapshot dropdown shows them so you can tell one import from another.
        </Callout>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h3 style={sectionTitle}>
          <Step n="2" /> How Report 9091 is prepared
        </h3>
        <Callout tone="blue">
          <strong>9091's</strong> uses the snapshot selected in the dropdown (the latest, if you have not picked one). It does not print the grid. It rebuilds Maestro's Summary Report — With Committed.
        </Callout>
        <ol style={{ margin: '12px 0 0', paddingLeft: '20px', color: '#374151', fontSize: '13px', lineHeight: '1.8' }}>
          <li><strong>Company</strong> — the Prefix column, matched to a division.</li>
          <li><strong>Discipline</strong> — the letters after the job number on Project (71683PIP → PIP).</li>
          <li><strong>Phase</strong> — split on the hyphen into a code and a description.</li>
          <li><strong>Activity</strong> — the same split. Several cost-type rows for the same activity are <strong>added into one line</strong>.</li>
          <li>Phase totals and the project total are the sum of those lines. One PDF per company. Each discipline starts on a new page.</li>
        </ol>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ ...sectionTitle, marginTop: '20px' }}>
          Data Mapping (Maestro import → Report 9091)
        </h3>
        <Callout tone="green">
          <strong>Green</strong> is copied from the import and summed for the activity. <strong>Blue</strong> is calculated after that sum. The on-screen grid has no blue columns — every grid column is the Maestro value.
        </Callout>
        <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>9091 column</th>
                <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Source</th>
              </tr>
            </thead>
            <tbody>
              <Row field="Activity" kind="maestro" source="✓ From Maestro Activity (code before the hyphen)" />
              <Row field="Description" kind="maestro" source="✓ From Maestro Activity (text after the hyphen)" />
              <Row field="Hours Est'd" kind="maestro" source="✓ Orig Bud (Q), summed for every cost type on the activity" />
              <Row field="Hours Actual" kind="maestro" source="✓ Actual (Q), labour rows only, then summed" />
              <Row field="Hours Variance" kind="calc" source="Calculated: Hours Est'd − Hours Actual" />
              <Row field="Revenue Est'd" kind="maestro" source="✓ Rev Selling Bud, summed" />
              <Row field="Revenue Actual" kind="maestro" source="✓ Billing, summed" />
              <Row field="Revenue Variance" kind="calc" source="Calculated: Revenue Est'd − Revenue Actual" />
              <Row field="Cost Est'd" kind="maestro" source="✓ Orig Bud, summed" />
              <Row field="Cost Actual" kind="maestro" source="✓ Actual, summed" />
              <Row field="Remaining PO Committed" kind="maestro" source="✓ PO Bal, summed" />
              <Row field="Total Cost Incl Committed" kind="maestro" source="✓ Total & PO, summed" />
              <Row field="Cost Variance" kind="calc" source="Calculated: Cost Est'd − Total Cost Incl Committed" />
              <Row field="Phase total / TOTAL PROJECT" kind="calc" source="Calculated: sum of the activity lines above" last />
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h3 style={sectionTitle}>
          <Step n="3" color="#dc3545" /> Priority Formula Sequence
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <FormulaBox tone="amber" title="Variances (after the activity is summed):">
            <div><strong>1.</strong> <code style={code('#fde68a')}>Hours Variance = Hours Est'd − Hours Actual</code></div>
            <div><strong>2.</strong> <code style={code('#fde68a')}>Revenue Variance = Revenue Est'd − Revenue Actual</code></div>
            <div><strong>3.</strong> <code style={code('#fde68a')}>Cost Variance = Cost Est'd − Total Cost Incl Committed</code></div>
          </FormulaBox>
          <FormulaBox tone="blue" title="TOTAL PROFIT (project footer, not a grid column):">
            <div><strong>1.</strong> <code style={code('#bfdbfe')}>Est'd Profit = Revenue Est'd − Cost Est'd</code></div>
            <div><strong>2.</strong> <code style={code('#bfdbfe')}>Actual Profit = Revenue Actual − Cost Actual</code></div>
            <div style={{ marginTop: '4px', fontStyle: 'italic' }}>Printed on the Revenue Est'd and Revenue Actual columns of the TOTAL PROFIT row.</div>
          </FormulaBox>
          <FormulaBox tone="green" title="Which rows count as hours:">
            <div>Hours Actual uses <strong>Actual (Q)</strong> only when the row is labour.</div>
            <div style={{ marginLeft: '16px' }}>
              <div>• Group column is <strong>L-Labour</strong>, or</div>
              <div>• older files with no Group: the phase name contains labour, labor, or staff</div>
            </div>
            <div style={{ marginTop: '6px' }}>These phases never count as hours, even if Group says labour: Room and Board, R&amp;B, Contract Expenses, Materials, Rentals, Site Expenses.</div>
            <div style={{ marginTop: '4px', fontStyle: 'italic' }}>Hours Est'd still sums Orig Bud (Q) for every cost type. Only Hours Actual is labour-only.</div>
          </FormulaBox>
          <FormulaBox tone="pink" title="Currency on the PDF:">
            <div>Dollar columns convert when that company's currency differs from the master project. Hours do not convert.</div>
            <div style={{ marginTop: '4px' }}><code style={code('#fbcfe8')}>printed dollars = Maestro dollars × exchange rate</code></div>
            <div style={{ marginTop: '4px', fontStyle: 'italic' }}>The footnote shows the rate. Phase and project totals sum the converted lines.</div>
          </FormulaBox>
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h3 style={sectionTitle}>
          <Step n="4" /> Calculated Fields &amp; Formulas
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormulaLine title="Hours Variance" formula="= Hours Est'd − Hours Actual" />
          <FormulaLine title="Revenue Variance" formula="= Revenue Est'd − Revenue Actual" />
          <FormulaLine title="Cost Variance" formula="= Cost Est'd − Total Cost Incl Committed" />
          <FormulaLine title="Est'd Profit" formula="= Revenue Est'd − Cost Est'd" />
          <FormulaLine title="Actual Profit" formula="= Revenue Actual − Cost Actual" />
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h3 style={sectionTitle}>
          <Step n="5" /> How the Consolidated report is prepared
        </h3>
        <Callout tone="blue">
          <strong>Consolidated</strong> uses the same snapshot as 9091. <strong>By Discipline</strong> keeps each discipline in its own section. <strong>Roll-Up</strong> uses the same columns, then merges a phase across every discipline and groups those phases into Contract, Extras, and Revenue.
        </Callout>
        <ol style={{ margin: '12px 0 0', paddingLeft: '20px', color: '#374151', fontSize: '13px', lineHeight: '1.8' }}>
          <li>Activity <strong>00105</strong> is dropped. It still prints on the per-company 9091.</li>
          <li>Each company (Prefix) becomes a pair of columns: <strong>Act. Hrs</strong> and <strong>Act Cost to Date</strong>.</li>
          <li>The <strong>TOTAL</strong> block adds those companies together for the activity.</li>
          <li>Phase totals add the activity lines. <strong>% Spent</strong>, <strong>Profit or Loss</strong>, and <strong>Est'd Add'l Bill.</strong> are worked out again from those totals. They are not a sum of the percents on the lines above.</li>
          <li>Roll-Up buckets: phase code under 80000 is Contract, 80000–89999 is Extras, 90000 and up is Revenue.</li>
        </ol>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ ...sectionTitle, marginTop: '20px' }}>
          Data Mapping (Maestro import → Consolidated)
        </h3>
        <Callout tone="green">
          <strong>Green</strong> is a Maestro column, summed for the activity. <strong>Blue</strong> is calculated after that sum. By Discipline and Roll-Up share this table. Only the grouping changes.
        </Callout>
        <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px' }}>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Consolidated column</th>
                <th style={{ textAlign: 'left', padding: '8px', color: '#374151' }}>Source</th>
              </tr>
            </thead>
            <tbody>
              <Row field="Project / Description" kind="maestro" source="✓ From Maestro Activity (code and text, split on the hyphen)" />
              <Row field="Act. Hrs (each company)" kind="maestro" source="✓ Actual (Q), labour rows only, for that Prefix" />
              <Row field="Act Cost to Date (each company)" kind="maestro" source="✓ Actual+Accrued, summed for that Prefix" />
              <Row field="Est'd Hours" kind="maestro" source="✓ Orig Bud (Q), labour rows only, all companies" />
              <Row field="Act. Hours" kind="maestro" source="✓ Actual (Q), labour rows only, all companies" />
              <Row field="Hrs Var." kind="calc" source="Calculated: Est'd Hours − Act. Hours" />
              <Row field="Contract Rev. Bud." kind="maestro" source="✓ Rev Selling Bud, summed. Not converted." />
              <Row field="Act. Rev. to Date" kind="maestro" source="✓ Billing, summed. Not converted." />
              <Row field="Est'd Cost" kind="maestro" source="✓ Orig Bud, summed. Converted to the master currency." />
              <Row field="Act Cost to Date" kind="maestro" source="✓ Actual+Accrued, summed. Converted to the master currency." />
              <Row field="Act Cost Incl POs" kind="maestro" source="✓ Total & PO, summed. Converted to the master currency." />
              <Row field="% Spent" kind="calc" source="Calculated: Act Cost Incl POs / Est'd Cost. Blank when Est'd Cost is zero." />
              <Row field="Profit or Loss" kind="calc" source="Calculated: Act. Rev. to Date − Act Cost Incl POs" />
              <Row field="Est'd Add'l Bill." kind="calc" source="Calculated: Contract Rev. Bud. − Act. Rev. to Date" />
              <Row field="Phase total" kind="calc" source="Calculated: sum of the activity lines, then Hrs Var., % Spent, Profit or Loss, and Est'd Add'l Bill. again" last />
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h3 style={sectionTitle}>
          <Step n="6" color="#dc3545" /> Consolidated formula sequence
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <FormulaBox tone="amber" title="After the activity is summed across companies:">
            <div><strong>1.</strong> <code style={code('#fde68a')}>Hrs Var. = Est'd Hours − Act. Hours</code></div>
            <div><strong>2.</strong> <code style={code('#fde68a')}>% Spent = Act Cost Incl POs / Est'd Cost</code></div>
            <div><strong>3.</strong> <code style={code('#fde68a')}>Profit or Loss = Act. Rev. to Date − Act Cost Incl POs</code></div>
            <div><strong>4.</strong> <code style={code('#fde68a')}>Est'd Add'l Bill. = Contract Rev. Bud. − Act. Rev. to Date</code></div>
            <div style={{ marginTop: '4px', fontStyle: 'italic' }}>% Spent is blank when Est'd Cost is zero. A phase total runs these four again on the summed dollars.</div>
          </FormulaBox>
          <FormulaBox tone="green" title="Hours on this report (both columns):">
            <div><strong>Est'd Hours</strong> and <strong>Act. Hours</strong> both count labour rows only.</div>
            <div style={{ marginTop: '4px' }}>On 9091, Hours Est'd still includes every quantity. Here it does not.</div>
            <div style={{ marginTop: '6px' }}>A row is labour when Group is <strong>L-Labour</strong>. Older files with no Group use a phase name that contains labour, labor, or staff. Room and Board, R&amp;B, Contract Expenses, Materials, Rentals, and Site Expenses never count as hours.</div>
          </FormulaBox>
          <FormulaBox tone="pink" title="Currency on this PDF:">
            <div>Only the three cost columns convert, when that company's currency differs from the master project: Est'd Cost, Act Cost to Date, and Act Cost Incl POs.</div>
            <div style={{ marginTop: '4px' }}>Contract Rev. Bud. and Act. Rev. to Date stay in the currency Maestro stored. Hours do not convert.</div>
            <div style={{ marginTop: '4px', fontStyle: 'italic' }}>The title says which currency the dollars are in. The footnote shows the cost rate when a company was converted.</div>
          </FormulaBox>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
          <FormulaLine title="Hrs Var." formula="= Est'd Hours − Act. Hours" />
          <FormulaLine title="% Spent" formula="= Act Cost Incl POs / Est'd Cost" />
          <FormulaLine title="Profit or Loss" formula="= Act. Rev. to Date − Act Cost Incl POs" />
          <FormulaLine title="Est'd Add'l Bill." formula="= Contract Rev. Bud. − Act. Rev. to Date" />
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h3 style={sectionTitle}>
          <Step n="7" /> The imported grid
        </h3>
        <Callout tone="slate">
          Every column under <strong>Imported Data</strong> — Project, Prefix, Phase, Activity, budgets, actuals, purchase orders, quantities, percents — is stored as Maestro exported it. Sort, filter, and column layout change the view. They do not change the numbers. <strong>View $</strong> opens in the project’s currency and converts other-currency rows on screen only. <strong>Native</strong> keeps each company in its own dollars. The project’s company comes from the Division on the project, else from the estimate number (<code>26-LG-…</code> Canada, <code>26-FL-…</code> LGL Inc.), else from the job-number prefix — hover the toggle to see which one was used.
        </Callout>
      </div>

      <CurrencySchematic />

      <div>
        <h3 style={sectionTitle}>
          <span style={{ backgroundColor: ACCENT, color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>💡</span>
          Tips
        </h3>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#374151', fontSize: '13px', lineHeight: '1.8' }}>
          <li>Type a <strong>cut-off date</strong> for the accounting date of the file, and a <strong>note</strong> such as “September month-end”. Both can be left blank.</li>
          <li>The dropdown is the upload time plus row count. When you entered a cut-off or a note, those show on that row too.</li>
          <li><strong>9091's</strong> is one PDF per company. <strong>By Discipline</strong> and <strong>Roll-Up</strong> share the consolidated columns. Roll-Up merges disciplines.</li>
          <li>The trash icon beside the snapshot dropdown removes that import. Audit records who deleted it, the cut-off, the note, and the row count.</li>
          <li>Click a column header to sort. Filters, Columns, and Auto-fit apply to the grid, not to the PDF.</li>
        </ul>
      </div>
    </>
  );
}

function CurrencySchematic() {
  return (
    <div style={{ marginBottom: '24px', padding: '18px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
      <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937', marginBottom: '14px' }}>
        💱 How CAD / USD flows — one export, two paths
      </div>
      <div style={{ border: '1.5px solid #475569', borderRadius: '8px', backgroundColor: '#fff', padding: '10px 14px', textAlign: 'center', maxWidth: '440px', margin: '0 auto' }}>
        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>Maestro export — one job, one file</div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
          <span style={{ padding: '3px 10px', borderRadius: '99px', backgroundColor: '#fee2e2', border: '1px solid #fecaca', fontSize: '11.5px', fontWeight: '600', color: '#991b1b' }}>Prefix · Canada · CAD</span>
          <span style={{ padding: '3px 10px', borderRadius: '99px', backgroundColor: '#dbeafe', border: '1px solid #bfdbfe', fontSize: '11.5px', fontWeight: '600', color: '#1e40af' }}>Prefix · USA · USD</span>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '14px' }}>
        <PathCard title="📄 9091 and Consolidated" steps={[
          ['Prefix → company → currency', 'Each row is tagged with its company’s currency'],
          ['Swap first — × exchange rate', 'Companies that differ from the master project convert'],
          ['Then count', 'One total, one currency — the footnote shows the rate'],
        ]} />
        <PathCard title="🧭 Import Data → Forecast tab" steps={[
          ['Reads rows with their prefix', 'The prefix identifies the company and its currency'],
          ['Swap first — × exchange rate', 'Dollar columns convert; the rate is stamped on the import'],
          ['Then roll up per operation code', 'Cost types and companies merge into one forecast row'],
        ]} />
      </div>
      <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '12px', color: '#1e3a5f', lineHeight: '1.6' }}>
        <strong>The piggy-bank rule:</strong> swap first, then count. Use <strong>View $</strong> to read the grid in one currency. Hours and quantities stay as they are.
      </div>
    </div>
  );
}

function PathCard({ title, steps }) {
  return (
    <div style={{ border: '1.5px solid #86efac', borderRadius: '10px', backgroundColor: '#fff', overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', backgroundColor: '#f0fdf4', borderBottom: '1px solid #bbf7d0', fontWeight: '600', fontSize: '13px', color: '#166534' }}>{title}</div>
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {steps.map(([t, sub], i) => (
          <div key={t} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0, width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#16a34a', color: '#fff', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
            <div>
              <div style={{ fontSize: '12.5px', fontWeight: '600', color: '#14532d' }}>{t}</div>
              <div style={{ fontSize: '11.5px', color: '#64748b', lineHeight: '1.5' }}>{sub}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const sectionTitle = {
  margin: '0 0 16px 0', color: '#111827', fontSize: '1.1rem',
  display: 'flex', alignItems: 'center', gap: '8px',
};

function Step({ n, color = ACCENT }) {
  return (
    <span style={{ backgroundColor: color, color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>{n}</span>
  );
}

function Card({ tone, icon, title, body }) {
  const tones = {
    blue: ['#f0f9ff', '#bae6fd', '#0369a1'],
    green: ['#f0fdf4', '#bbf7d0', '#15803d'],
    amber: ['#fef3c7', '#fcd34d', '#a16207'],
  };
  const [bg, border, color] = tones[tone];
  return (
    <div style={{ padding: '16px', backgroundColor: bg, borderRadius: '8px', textAlign: 'center', border: `1px solid ${border}` }}>
      <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>
      <div style={{ fontWeight: '600', color, marginBottom: '4px' }}>{title}</div>
      <div style={{ fontSize: '12px', color: '#6b7280' }}>{body}</div>
    </div>
  );
}

function Callout({ tone, children }) {
  const tones = {
    amber: ['#fef3c7', '#fcd34d', '#92400e'],
    blue: ['#eff6ff', '#bfdbfe', '#1e3a5f'],
    green: ['#ecfdf5', '#a7f3d0', '#065f46'],
    slate: ['#f8fafc', '#e2e8f0', '#334155'],
  };
  const [bg, border, color] = tones[tone];
  return (
    <div style={{ padding: '12px 16px', backgroundColor: bg, border: `1px solid ${border}`, borderRadius: '8px', fontSize: '13px', color, lineHeight: '1.6' }}>
      {children}
    </div>
  );
}

function Row({ field, kind, source, last }) {
  return (
    <tr style={{ borderBottom: last ? 'none' : '1px solid #e5e7eb' }}>
      <td style={{ padding: '8px' }}>{field}</td>
      {sourceCell(kind, source)}
    </tr>
  );
}

function FormulaBox({ tone, title, children }) {
  const tones = {
    amber: ['#fef3c7', '#fcd34d', '#92400e', '#78350f'],
    blue: ['#dbeafe', '#93c5fd', '#1e40af', '#1e3a5f'],
    green: ['#dcfce7', '#86efac', '#166534', '#14532d'],
    pink: ['#fce7f3', '#f9a8d4', '#9d174d', '#831843'],
  };
  const [bg, border, titleColor, bodyColor] = tones[tone];
  return (
    <div style={{ padding: '16px', backgroundColor: bg, borderRadius: '8px', border: `1px solid ${border}` }}>
      <div style={{ fontWeight: '700', color: titleColor, marginBottom: '8px', fontSize: '14px' }}>{title}</div>
      <div style={{ fontSize: '13px', color: bodyColor, lineHeight: '1.8' }}>{children}</div>
    </div>
  );
}

function code(bg) {
  return { backgroundColor: bg, padding: '2px 6px', borderRadius: '4px' };
}

function FormulaLine({ title, formula }) {
  return (
    <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', borderLeft: `4px solid ${ACCENT}` }}>
      <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>{title}</div>
      <code style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#e5e7eb', padding: '2px 6px', borderRadius: '4px' }}>{formula}</code>
    </div>
  );
}

export default MaestroDashboardHelp;
